import { App as CapApp } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

const normalizeAppVersion = (value?: string | null): string | null => {
  if (value === undefined || value === null) return null;

  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
};

const toVersionParts = (value: string): number[] => {
  const matches = value.match(/\d+/g);
  if (!matches?.length) {
    return [];
  }

  return matches.map((part) => Number.parseInt(part, 10));
};

let cachedAppVersionPromise: Promise<string | null> | null = null;

export const getCurrentAppVersion = async (): Promise<string | null> => {
  if (!cachedAppVersionPromise) {
    cachedAppVersionPromise = (async () => {
      if (Capacitor.isNativePlatform()) {
        try {
          const info = await CapApp.getInfo();
          return normalizeAppVersion(info.version);
        } catch (error) {
          console.warn('Failed to read Capacitor app version:', error);
        }
      }

      return normalizeAppVersion(process.env.REACT_APP_APPLICATION_VERSION ?? null);
    })();
  }

  return cachedAppVersionPromise;
};

export const compareAppVersions = (
  currentVersion?: string | null,
  targetVersion?: string | null,
): number | null => {
  const normalizedCurrent = normalizeAppVersion(currentVersion);
  const normalizedTarget = normalizeAppVersion(targetVersion);

  if (!normalizedCurrent || !normalizedTarget) {
    return null;
  }

  const currentParts = toVersionParts(normalizedCurrent);
  const targetParts = toVersionParts(normalizedTarget);
  const length = Math.max(currentParts.length, targetParts.length);

  for (let index = 0; index < length; index += 1) {
    const left = currentParts[index] ?? 0;
    const right = targetParts[index] ?? 0;

    if (left > right) return 1;
    if (left < right) return -1;
  }

  return 0;
};

export const isAppVersionBehind = (
  currentVersion?: string | null,
  targetVersion?: string | null,
): boolean => compareAppVersions(currentVersion, targetVersion) === -1;
